import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useSession } from '../App';
import { ApiError, api, useJobPolling, useQuery } from '../api/client';
import type { Audit, CategoryAssessment } from '../api/types';
import { TranscriptView, buildEvidenceIndex } from '../components/Transcript';
import {
  BandPill,
  Card,
  Empty,
  ErrorNote,
  EvidenceList,
  EvidenceQuote,
  IconChevron,
  ProvenanceTags,
  SCORE_WORD,
  ScaleBar,
  SeverityPill,
  Skeleton,
  VerdictPill,
  toneForBant,
  toneForScore,
} from '../components/ui';
import { formatDateTime, formatDuration, localTimeIn, titleCase } from '../lib/format';

const TABS = ['scorecard', 'bant', 'integrity', 'coaching', 'transcript', 'recording', 'highlights', 'trace'] as const;
type Tab = (typeof TABS)[number];

const TAB_LABEL: Record<Tab, string> = {
  scorecard: 'Scorecard',
  bant: 'BANT & status',
  integrity: 'Integrity',
  coaching: 'Coaching',
  transcript: 'Transcript',
  recording: 'Recording',
  highlights: 'Highlights',
  trace: 'Audit trail',
};

export function CallAuditPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  // Preserve the filters the user had applied on the calls list: Calls.tsx
  // hands its full URL (query string included) in nav state when it opens a
  // row, so "Back to calls" returns to the same filtered view instead of a
  // reset list. Falls back to a plain /calls when there is no nav state, e.g.
  // the page was opened directly from a bookmark or shared link.
  const backHref = (location.state as { from?: string } | null)?.from || '/calls';
  const { user } = useSession();
  const [tab, setTab] = useState<Tab>('scorecard');
  const [targetIdx, setTargetIdx] = useState<number | null>(null);

  const { data: callData, reload: reloadCall } = useQuery(() => api.call(id), [id]);
  const processing = callData?.call.processingStatus === 'pending' || callData?.call.processingStatus === 'processing';
  const { job } = useJobPolling(id, Boolean(processing));

  const auditQuery = useQuery(() => api.audit(id), [id, job?.status === 'complete' ? 'done' : 'pending'], !processing);
  const audit = auditQuery.data?.audit ?? null;

  const evidenceIndex = useMemo(() => (audit ? buildEvidenceIndex(audit) : new Map()), [audit]);

  const jumpToEvidence = useCallback((segmentIdx: number | null) => {
    if (segmentIdx === null) return;
    setTab('transcript');
    // Force a fresh target so jumping twice to the same line still scrolls.
    setTargetIdx(null);
    requestAnimationFrame(() => setTargetIdx(segmentIdx));
  }, []);

  if (processing || job?.status === 'running' || job?.status === 'queued') {
    return <ProcessingView job={job} onDone={reloadCall} />;
  }

  if (job?.status === 'failed' || callData?.call.processingStatus === 'failed') {
    return (
      <FailedView
        error={job?.error ?? 'The audit could not be completed.'}
        stage={job?.error_stage ?? null}
        callId={id}
        onRetry={reloadCall}
        backHref={backHref}
      />
    );
  }

  if (auditQuery.loading && !audit) {
    return (
      <div className="page-body">
        <Skeleton height={92} />
        <div style={{ height: 14 }} />
        <Skeleton height={280} />
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="page-body">
        <ErrorNote error={auditQuery.error} onRetry={auditQuery.reload} />
        <Card>
          <Empty
            title="No audit for this call yet"
            body="The audit has not been produced. Run it from the calls list, or check the processing status."
            action={
              <Link to={backHref} className="btn">
                Back to calls
              </Link>
            }
          />
        </Card>
      </div>
    );
  }

  const flagCount = audit.integrityFlags.length;

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>{audit.prospect.name ?? 'Unnamed prospect'}</h1>
          <p className="page-sub">
            {audit.prospect.company ?? 'No company recorded'} · {audit.sdr} · {formatDateTime(audit.date)}
          </p>
        </div>
        <div className="page-head-actions">
          <Link to={backHref} className="btn btn-ghost btn-sm">
            ← All calls
          </Link>
        </div>
      </header>

      <div className="page-body page-enter">
        {audit.autoFail.triggered && <AutoFailBanner audit={audit} onJump={jumpToEvidence} />}

        <AuditHeader audit={audit} logType={(callData?.call.logType as string | null | undefined) ?? null} />

        <div className="tabs" role="tablist">
          {TABS.map((t) => {
            if ((t === 'trace' || t === 'highlights') && user?.role === 'sdr') return null;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                className="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
              >
                {TAB_LABEL[t]}
                {t === 'integrity' && flagCount > 0 && <span className="tab-count-alert">{flagCount}</span>}
                {t === 'transcript' && <span className="tab-count">{audit.transcript.segments.length}</span>}
              </button>
            );
          })}
        </div>

        {tab === 'scorecard' && <Scorecard audit={audit} onJump={jumpToEvidence} />}
        {tab === 'bant' && (
          <BantPanel audit={audit} onJump={jumpToEvidence} logType={(callData?.call.logType as string | null | undefined) ?? null} />
        )}
        {tab === 'integrity' && <IntegrityPanel audit={audit} onJump={jumpToEvidence} />}
        {tab === 'coaching' && <CoachingPanel audit={audit} />}
        {tab === 'transcript' && (
          <TranscriptView audit={audit} evidenceIndex={evidenceIndex} targetIdx={targetIdx} onClearTarget={() => setTargetIdx(null)} />
        )}
        {tab === 'recording' && (
          <RecordingPanel
            recordingUrl={(callData?.call.recordingUrl as string | null | undefined) ?? null}
            logType={(callData?.call.logType as string | null | undefined) ?? null}
          />
        )}
        {tab === 'highlights' && user?.role !== 'sdr' && <HighlightsPanel callId={id} sdrName={audit.sdr} />}
        {tab === 'trace' && user?.role !== 'sdr' && <TracePanel audit={audit} callId={id} />}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Processing / failure
 * ------------------------------------------------------------------ */

const STAGE_ORDER = [
  'validating',
  'transcribing',
  'speakers',
  'extracting',
  'gap_fill',
  'bant',
  'assessing',
  'integrity',
  'autofail',
  'scoring',
  'status',
  'coaching',
  'finalizing',
];
const STAGE_LABEL: Record<string, string> = {
  validating: 'Validating input',
  transcribing: 'Preparing transcript',
  speakers: 'Identifying speakers',
  extracting: 'Extracting evidence',
  gap_fill: 'Re-checking gaps',
  bant: 'Running BANT',
  assessing: 'Running QA scorecard',
  integrity: 'Checking integrity',
  autofail: 'Evaluating auto-fail',
  scoring: 'Calculating score',
  status: 'Reconciling status',
  coaching: 'Generating coaching',
  finalizing: 'Finalizing report',
};

export function ProcessingView({ job, onDone }: { job: { stage: string | null; detail: string | null; status: string } | null; onDone: () => void }) {
  const current = job?.stage ? STAGE_ORDER.indexOf(job.stage) : -1;
  if (job?.status === 'complete') onDone();

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>Auditing the call</h1>
          <p className="page-sub">Each stage grounds its findings in the transcript before the next one runs.</p>
        </div>
      </header>
      <div className="page-body page-enter">
        <Card title="Progress">
          <div className="progress-list">
            {STAGE_ORDER.map((s, i) => {
              const done = current > i;
              const active = current === i;
              return (
                <div key={s} className={`progress-step ${done ? 'is-done' : ''} ${active ? 'is-active' : ''}`}>
                  <span className="progress-mark" aria-hidden>
                    {done ? '✓' : ''}
                  </span>
                  <span>{STAGE_LABEL[s]}</span>
                  {active && job?.detail && <span className="progress-detail">{job.detail}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </>
  );
}

function FailedView({
  error,
  stage,
  callId,
  onRetry,
  backHref,
}: {
  error: string;
  stage: string | null;
  callId: string;
  onRetry: () => void;
  backHref: string;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>The audit did not complete</h1>
          <p className="page-sub">No partial result was kept. Nothing was scored on incomplete evidence.</p>
        </div>
      </header>
      <div className="page-body page-enter">
        <Card title={stage ? `Failed at: ${STAGE_LABEL[stage] ?? titleCase(stage)}` : 'Failed'}>
          <div className="alert" role="alert">
            {error}
          </div>
          <p className="muted mt-3">
            The pipeline stops rather than publishing an audit built on missing evidence. Fix the cause and run it again — the
            call, its metadata and its transcript are all still on file.
          </p>
          <div className="row mt-3">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await api.process(callId).catch(() => {});
                onRetry();
                setBusy(false);
              }}
            >
              {busy ? 'Starting…' : 'Run the audit again'}
            </button>
            <Link to={backHref} className="btn">
              Back to calls
            </Link>
          </div>
        </Card>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

function AuditHeader({ audit, logType }: { audit: Audit; logType: string | null }) {
  const localTime = localTimeIn(audit.date, audit.prospect.timezone);
  return (
    <section className="card audit-head">
      <div>
        <div className="meta-label">Overall score</div>
        <div className="audit-score">
          <span className={`audit-score-num score-${toneForScore(audit.overallScore)}`}>
            {audit.overallScore === null ? '—' : audit.overallScore.toFixed(1)}
          </span>
          <span className="dim">/ 5</span>
        </div>
        <div className="mt-2">
          <BandPill band={audit.band} />
        </div>
      </div>

      <div className="audit-meta" style={{ flex: 1 }}>
        <div className="meta-item">
          <div className="meta-label">Prospect name</div>
          <div className="meta-value">{audit.prospect.name ?? <span className="unavailable">Not recorded</span>}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Phone number</div>
          <div className="meta-value">{audit.prospect.phone ?? <span className="unavailable">Not recorded</span>}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">SDR</div>
          <div className="meta-value">{audit.sdr}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Prospect type</div>
          <div className="meta-value">{titleCase(audit.prospect.type)}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Market</div>
          <div className="meta-value">{audit.prospect.market ?? <span className="unavailable">Not recorded</span>}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Duration</div>
          <div className="meta-value">{formatDuration(audit.metrics.durationSec)}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Prospect local time</div>
          <div className="meta-value">{localTime ?? <span className="unavailable">Timezone unknown</span>}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Auditor verdict</div>
          <div className="meta-value">{audit.status.agentVerdictStatus}</div>
        </div>
        <div className="meta-item">
          <div className="meta-label">SDR logged</div>
          <div className="meta-value">
            {audit.status.sdrLoggedStatus ?? (
              logType ? (
                <span className="unavailable" title="Dial-outcome disposition, not a qualification claim">
                  {logType}
                </span>
              ) : (
                <span className="unavailable">Not logged</span>
              )
            )}
            {audit.status.statusMatches === false && <span className="pill pill-sm pill-fail" style={{ marginLeft: 6 }}>Mismatch</span>}
          </div>
        </div>
        <div className="meta-item">
          <div className="meta-label">Qualified booking</div>
          <div className="meta-value">
            {audit.bant.qualifiedBooking ? (
              <span className="pill pill-excellent">Yes</span>
            ) : (
              <span className="pill pill-warn">No</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function AutoFailBanner({ audit, onJump }: { audit: Audit; onJump: (idx: number | null) => void }) {
  const af = audit.autoFail;
  const seg = audit.integrityFlags.find((f) => f.type === af.allTriggers[0]?.flagType)?.evidence?.segmentIdx ?? null;
  // Several findings can raise the same Section 6 trigger; list each trigger once.
  const distinctTriggers = [...new Set(af.allTriggers.map((t) => t.label))];
  return (
    <section className="fail-banner" role="alert">
      <div className="fail-banner-head">
        <span className="fail-tag">FAIL</span>
        <span className="fail-trigger">{af.allTriggers[0]?.label ?? 'Auto-fail triggered'}</span>
      </div>
      <p className="fail-reason">{af.reason}</p>
      {af.quote && (
        <EvidenceQuote
          evidence={{ quote: af.quote, segmentIdx: seg, timestamp: af.timestamp, speaker: 'SDR', verified: true, matchMethod: 'exact' }}
          onJump={seg !== null ? () => onJump(seg) : undefined}
        />
      )}
      <p className="fail-more">
        {af.explanation} An auto-fail overrides the numerical score entirely, and is escalated for manager review.
        {distinctTriggers.length > 1 &&
          ` ${distinctTriggers.length} different triggers fired on this call: ${distinctTriggers.join(', ')}.`}
        {distinctTriggers.length === 1 && af.allTriggers.length > 1 &&
          ` ${af.allTriggers.length} findings fired this trigger: ${af.allTriggers.map((t) => t.reason.split(':')[0]).join(', ')}.`}
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Scorecard
 * ------------------------------------------------------------------ */

function Scorecard({ audit, onJump }: { audit: Audit; onJump: (idx: number | null) => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const order = audit.scoring.breakdown.map((b) => b.key);

  return (
    <div className="stack">
      <section className="card">
        <div className="verdict-banner">
          <div>
            <span className="meta-label">The arithmetic</span>
            <div className="mt-2 small">
              Average of {audit.scoring.scoredCount} scored categories
              {audit.scoring.naCount > 0 && `, with ${audit.scoring.naCount} excluded as N/A`} ={' '}
              <strong>{audit.overallScore ?? '—'}</strong>, which is the &ldquo;{audit.scoring.rawBand}&rdquo; band.
            </div>
            {audit.scoring.capReason && <div className="small muted mt-2">{audit.scoring.capReason}</div>}
            {audit.scoring.autoFailApplied && (
              <div className="small mt-2" style={{ color: 'var(--c-fail)' }}>
                An auto-fail trigger fired, which overrides the band entirely. Final band: FAIL.
              </div>
            )}
          </div>
        </div>

        {order.map((key) => {
          const c = audit.categoryScores[key];
          if (!c) return null;
          const b = audit.scoring.breakdown.find((x) => x.key === key)!;
          const expanded = open === key;
          return (
            <div className="cat" key={key}>
              <button type="button" className="cat-head" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : key)}>
                <span className="cat-letter">{b.letter}</span>
                <span className="cat-title">
                  {b.title}
                  {b.critical && (
                    <span className="critical-mark" title="Critical category: a score of 1 caps the band at Needs Work">
                      ★
                    </span>
                  )}
                  {c.evidenceUnavailable && <span className="pill pill-sm">Evidence unavailable</span>}
                  {c.overriddenBy && <span className="pill pill-sm pill-warn">Rule applied</span>}
                </span>
                <span className="cat-right">
                  <ScaleBar score={c.na ? null : c.score} />
                  <span className="nowrap" style={{ minWidth: 118, textAlign: 'right' }}>
                    {c.na ? (
                      <span className="unavailable">N/A</span>
                    ) : (
                      <>
                        <strong className={`score-${toneForScore(c.score)}`}>{c.score}</strong>
                        <span className="dim small"> · {SCORE_WORD[c.score ?? 3]}</span>
                      </>
                    )}
                  </span>
                  <span className="chev">
                    <IconChevron />
                  </span>
                </span>
              </button>

              {expanded && (
                <div className="cat-body">
                  <p className="muted small">{c.rule}</p>
                  {c.note && <p>{c.note}</p>}

                  {c.overriddenBy && (
                    <div className="override">
                      <span className="override-label">Deterministic rule applied</span>
                      {c.overriddenBy}
                      {c.aiScore !== null && c.aiScore !== c.score && (
                        <div className="small mt-2 muted">
                          The analysis layer scored this {c.aiScore}. The rule above set it to {c.score ?? 'N/A'}.
                        </div>
                      )}
                    </div>
                  )}

                  {c.checks.length > 0 && (
                    <div>
                      <div className="meta-label mb-2">Checks</div>
                      <div className="checks">
                        {c.checks.map((chk, i) => (
                          <div className="check" key={i}>
                            <span className={`check-icon check-${chk.result}`}>
                              {chk.result === 'pass' ? '✓' : chk.result === 'fail' ? '✕' : chk.result === 'partial' ? '~' : '?'}
                            </span>
                            <span className="check-label">{chk.label}</span>
                            <span className="check-detail">{chk.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="meta-label mb-2">Evidence from the call</div>
                    <EvidenceList evidence={c.evidence} onJump={(idx) => onJump(idx)} />
                  </div>

                  <ProvenanceTags provenance={c.provenance} />
                </div>
              )}
            </div>
          );
        })}
      </section>

      <MetricsCard audit={audit} />

      {audit.limitations.length > 0 && (
        <Card title="What this audit could not evaluate" note={`${audit.limitations.length}`}>
          <div className="limitations">
            {audit.limitations.map((l, i) => (
              <div className="limitation" key={i}>
                <div className="limitation-area">{l.area}</div>
                <div className="muted">{l.reason}</div>
                <div className="limitation-impact">{l.impact}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function MetricsCard({ audit }: { audit: Audit }) {
  const m = audit.metrics;
  return (
    <Card title="Measured call metrics" note={m.estimated ? 'Estimated from word counts' : 'Measured from timestamps'}>
      <div className="grid grid-stats">
        <div>
          <div className="meta-label">SDR share of talk time</div>
          <div className="stat-value" style={{ fontSize: 20 }}>
            {m.sdrTalkPct === null ? <span className="unavailable">Unavailable</span> : `${m.sdrTalkPct}%`}
          </div>
        </div>
        <div>
          <div className="meta-label">Questions asked</div>
          <div className="stat-value" style={{ fontSize: 20 }}>
            {m.questionsAsked}
            <span className="dim small"> ({m.openQuestionsAsked} open)</span>
          </div>
        </div>
        <div>
          <div className="meta-label">Longest stretch without a question</div>
          <div className="stat-value" style={{ fontSize: 20 }}>
            {m.longestMonologueSeconds}s
          </div>
          <div className="stat-meta">Guideline is ~20s</div>
        </div>
        <div>
          <div className="meta-label">Turns</div>
          <div className="stat-value" style={{ fontSize: 20 }}>
            {m.turnCount}
          </div>
        </div>
      </div>
      {m.estimated && (
        <p className="dim small mt-3">
          The transcript carries no timestamps, so these durations are estimated from word counts at a fixed speaking rate. They
          are indicative, not measured.
        </p>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * BANT & status
 * ------------------------------------------------------------------ */

const FACTOR_META = [
  { key: 'budget', letter: 'B', name: 'Budget' },
  { key: 'authority', letter: 'A', name: 'Authority' },
  { key: 'need', letter: 'N', name: 'Need' },
  { key: 'timeframe', letter: 'T', name: 'Time frame' },
] as const;

function BantPanel({ audit, onJump, logType }: { audit: Audit; onJump: (idx: number | null) => void; logType: string | null }) {
  const s = audit.status;
  return (
    <div className="stack">
      <section className="card">
        <div className="verdict-banner">
          <div>
            <span className="meta-label">Booking bar</span>
            <div className="mt-2">
              {audit.bant.qualifiedBooking ? (
                <span className="pill pill-excellent">Qualified booking</span>
              ) : (
                <span className="pill pill-warn">Not a qualified booking</span>
              )}
            </div>
          </div>
          <p className="small muted" style={{ flex: 1, minWidth: 220 }}>
            {audit.bant.qualifiedBookingRule}
          </p>
        </div>

        <div className="bant">
          {FACTOR_META.map((meta) => {
            const f = audit.bant[meta.key];
            const tone = toneForBant(f.verdict, f.evidenceUnavailable);
            return (
              <div className="bant-factor" key={meta.key}>
                <div className="bant-head">
                  <span
                    className={`bant-letter pill-${tone}`}
                    style={{ borderColor: 'currentColor', background: 'transparent' }}
                  >
                    {meta.letter}
                  </span>
                  <span className="bant-name">{meta.name}</span>
                  <VerdictPill verdict={f.verdict} />
                  {f.evidenceUnavailable && <span className="pill pill-sm">Never established</span>}
                </div>
                <p className="bant-reason">{f.reason}</p>
                <EvidenceList
                  evidence={f.evidence}
                  onJump={(idx) => onJump(idx)}
                  emptyMessage="Evidence unavailable — nothing in the call establishes this factor either way."
                />
                <div className="bant-rule">
                  <strong>Rule:</strong> {f.rule}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <Card title="Status reconciliation" note="The auditor verdict is computed independently of what the SDR logged">
        <div className="recon">
          <div className="recon-side">
            <div className="recon-side-label">SDR logged status</div>
            <div className="recon-status">
              {s.sdrLoggedStatus ??
                (logType ? (
                  <span className="unavailable" title="Dial-outcome disposition, not a qualification claim">
                    {logType}
                  </span>
                ) : (
                  <span className="unavailable">Not logged</span>
                ))}
            </div>
            <div className="recon-source">What the SDR / CRM says happened</div>
          </div>
          <div className="recon-mid">
            {s.statusMatches === null ? (
              <span className="pill">No comparison</span>
            ) : s.statusMatches ? (
              <span className="pill pill-excellent">Match</span>
            ) : (
              <span className="pill pill-fail">Mismatch</span>
            )}
          </div>
          <div className="recon-side">
            <div className="recon-side-label">Auditor verdict</div>
            <div className="recon-status">{s.agentVerdictStatus}</div>
            <div className="recon-source">What the evidence indicates happened</div>
          </div>
        </div>

        <div className="bant-rule mt-3">
          <strong>Rule:</strong> {s.verdictRule}
          <div className="mt-2">{s.verdictReason}</div>
        </div>

        {s.mismatchExplanation && (
          <div className={`alert ${s.statusMatches === false ? '' : 'alert-info'} mt-3`}>{s.mismatchExplanation}</div>
        )}

        {s.misQualification && (
          <div className="alert mt-3">
            <strong>Mis-qualification.</strong> The logged record claims a qualified prospect while the transcript shows a BANT
            failure. Per the standard, categories D and H drop to 1 and this is a fraudulent-record auto-fail.
          </div>
        )}
      </Card>

      <ExtractionCard audit={audit} onJump={onJump} />
    </div>
  );
}

function ExtractionCard({ audit, onJump }: { audit: Audit; onJump: (idx: number | null) => void }) {
  const fields = Object.values(audit.extraction);
  const found = fields.filter((f) => f.state === 'VALUE');
  const refused = fields.filter((f) => f.state === 'REFUSED');
  const unclear = fields.filter((f) => f.state === 'UNCLEAR');
  const missing = fields.filter((f) => f.state === 'NOT_MENTIONED');

  return (
    <Card title="What the call established" note={`${found.length} of ${fields.length} fields`} bodyless>
      <div className="card-body">
        <p className="dim small mb-2">
          Three distinct missing-states are tracked separately: a topic that never came up is not the same as one the prospect
          declined to answer, or one the audio garbled.
        </p>
      </div>
      <div className="table-wrap" style={{ border: 0, borderRadius: 0, boxShadow: 'none' }}>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
              <th>Source</th>
              <th>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {[...found, ...refused, ...unclear, ...missing].map((f) => (
              <tr key={f.key}>
                <td className="cell-primary nowrap">{f.label}</td>
                <td>
                  {f.state === 'VALUE' ? (
                    f.value
                  ) : f.state === 'REFUSED' ? (
                    <span className="pill pill-sm pill-warn">Refused to discuss</span>
                  ) : f.state === 'UNCLEAR' ? (
                    <span className="pill pill-sm pill-warn">Unclear</span>
                  ) : (
                    <span className="unavailable">Not mentioned</span>
                  )}
                  {f.rejectedValue && (
                    <div className="small" style={{ color: 'var(--c-fail)', marginTop: 3 }}>
                      Rejected as ungrounded: &ldquo;{f.rejectedValue}&rdquo; — no matching quote in the transcript.
                    </div>
                  )}
                </td>
                <td className="nowrap small dim">
                  {f.state === 'NOT_MENTIONED' ? '—' : titleCase(f.source)}
                  {f.pass === 2 && <span className="pill pill-sm" style={{ marginLeft: 5 }}>2nd pass</span>}
                </td>
                <td>
                  {f.evidence ? (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => onJump(f.evidence!.segmentIdx)}>
                      {f.evidence.timestamp ?? 'View'} →
                    </button>
                  ) : (
                    <span className="dim">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Integrity
 * ------------------------------------------------------------------ */

function IntegrityPanel({ audit, onJump }: { audit: Audit; onJump: (idx: number | null) => void }) {
  const flags = audit.integrityFlags;
  const scanFailed = audit.limitations.some((l) => l.area === 'Integrity scan' && l.impact.includes('do not read the absence'));

  return (
    <div className="stack">
      {scanFailed && (
        <div className="alert alert-warn">
          The language-model integrity scan did not run on this call. Only the deterministic checks were applied — the absence of
          honesty or brand flags below does <strong>not</strong> mean the call was clean.
        </div>
      )}

      <Card title="Integrity & abuse detection" note={flags.length ? `${flags.length} flag${flags.length === 1 ? '' : 's'}` : 'No flags'} bodyless>
        {flags.length === 0 ? (
          <div className="card-body">
            <p className="muted">
              No integrity flags were raised. The booking bar, the logged-status comparison, the calling-hours window, the pricing
              schedule and the transcript metrics were all checked.
            </p>
          </div>
        ) : (
          flags.map((f, i) => (
            <div className="flag" key={`${f.type}-${i}`}>
              <div className="flag-sev">
                <SeverityPill severity={f.severity} />
              </div>
              <div className="flag-body">
                <div className="flag-title">{f.label}</div>
                <div className="flag-exp">{f.explanation}</div>
                {f.evidence ? (
                  <EvidenceQuote evidence={f.evidence} onJump={() => onJump(f.evidence!.segmentIdx)} />
                ) : f.quote ? (
                  <div className="evidence">
                    <span className="evidence-meta">
                      {f.timestamp && <span className="evidence-time">{f.timestamp}</span>}
                    </span>
                    <span className="evidence-quote">&ldquo;{f.quote}&rdquo;</span>
                  </div>
                ) : (
                  <div className="evidence-empty">
                    Detected from call records rather than a spoken line, so there is no transcript quote to show.
                  </div>
                )}
                <div className="flag-tags">
                  <span className="pill pill-sm">
                    {f.detectedBy === 'deterministic'
                      ? 'Application rule'
                      : f.detectedBy === 'cross_call'
                        ? 'Cross-call pattern'
                        : 'AI, quote verified'}
                  </span>
                  <span className="pill pill-sm">{titleCase(f.group)}</span>
                  {!f.evidenceBacked && (
                    <span
                      className="pill pill-sm pill-warn"
                      title="Raised because something could not be established, not because of a verified violation. It does not trigger an auto-fail."
                    >
                      Not evidence-backed
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </Card>

      <Card title="Auto-fail evaluation">
        {audit.autoFail.triggered ? (
          <div className="stack">
            {audit.autoFail.allTriggers.map((t, i) => (
              <div className="alert" key={i}>
                <strong>{t.label}</strong>
                <div className="mt-2">{t.reason}</div>
                {t.quote && (
                  <div className="mt-2 small">
                    {t.timestamp && <span className="mono">{t.timestamp} </span>}&ldquo;{t.quote}&rdquo;
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">
            No auto-fail trigger fired. A trigger requires an evidence-backed finding: a compliance breach, dishonesty that
            reached the prospect, a fraudulent qualification record, an unapproved commercial promise, or abusive conduct.
          </p>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Coaching
 * ------------------------------------------------------------------ */

function CoachingPanel({ audit }: { audit: Audit }) {
  const c = audit.coaching;
  return (
    <div className="stack">
      <div className="coach">
        <div className="coach-card is-win">
          <div className="coach-label">One win</div>
          <p className="coach-text">{c.oneWin}</p>
        </div>
        <div className="coach-card is-fix">
          <div className="coach-label">One fix</div>
          <p className="coach-text">{c.oneFix}</p>
        </div>
        <div className={`coach-card ${c.priorityItems.length ? 'is-priority' : ''}`}>
          <div className="coach-label">Priority</div>
          <p className="coach-text">{c.priorityItems.length ? 'Routed to the manager' : c.priority}</p>
          {c.priorityItems.length > 0 && (
            <ul className="coach-items">
              {c.priorityItems.map((p, i) => (
                <li key={i}>
                  <strong>{p.label}</strong> — {p.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {c.gaps.length > 0 && (
        <Card title="What the call failed to establish" note="The highest-leverage gaps">
          <ul className="coach-items" style={{ fontSize: 13.5 }}>
            {c.gaps.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </Card>
      )}

      {c.generatedBy === 'deterministic_fallback' && (
        <div className="alert alert-warn">
          The coaching above was derived from the scored findings rather than written by the analysis layer, which did not
          respond on this call.
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Recording
 * ------------------------------------------------------------------ */

function RecordingPanel({ recordingUrl, logType }: { recordingUrl: string | null; logType: string | null }) {
  if (!recordingUrl) {
    return (
      <Card>
        <Empty title="No recording link" body="This call has no recording link on file - it may have been submitted with a transcript directly." />
      </Card>
    );
  }

  const isGoogleDrive = /drive\.google\.com/i.test(recordingUrl);

  return (
    <div className="stack">
      <Card title="Call recording" note={logType ?? undefined}>
        {isGoogleDrive ? (
          <div className="stack" style={{ gap: 10 }}>
            <p className="muted" style={{ margin: 0 }}>
              This recording is hosted on Google Drive and opens in a new tab.
            </p>
            <a href={recordingUrl} target="_blank" rel="noreferrer" className="btn btn-sm">
              Open recording ↗
            </a>
          </div>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio controls src={recordingUrl} style={{ width: '100%' }} />
            <a href={recordingUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
              Open in a new tab ↗
            </a>
          </div>
        )}
        <p className="small muted mt-2" style={{ wordBreak: 'break-all' }}>
          {recordingUrl}
        </p>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Highlights - flag a call for coaching, with a note for the SDR's profile
 * ------------------------------------------------------------------ */

function HighlightsPanel({ callId, sdrName }: { callId: string; sdrName: string }) {
  const { data, loading, error, reload } = useQuery(() => api.highlight(callId), [callId]);
  const [checked, setChecked] = useState(false);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    setChecked(data.highlighted);
    setComment(data.comment);
    setJustSaved(false);
  }, [data]);

  const dirty = data ? checked !== data.highlighted || comment !== data.comment : false;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await api.setHighlight(callId, { highlighted: checked, comment });
      setJustSaved(true);
      reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save this highlight.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="stack">
      <Card
        title="Coaching highlight"
        note="Flag this call for the SDR's Coaching Points profile, with a note on why it's worth reviewing."
      >
        <ErrorNote error={error} onRetry={reload} />
        {loading && !data ? (
          <Skeleton height={110} />
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Add this call to <strong>{sdrName}</strong>'s Coaching Points profile
              </span>
            </label>
            <div>
              <label className="meta-label" htmlFor="highlight-comment">
                Coaching notes
              </label>
              <textarea
                id="highlight-comment"
                rows={5}
                placeholder="What should the SDR take away from this call?"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <button type="button" className="btn btn-primary" disabled={saving || !dirty} onClick={() => void save()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {!dirty && justSaved && <span className="small muted">Saved.</span>}
              {saveError && (
                <span className="small" style={{ color: 'var(--c-fail)' }}>
                  {saveError}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Audit trail (managers only)
 * ------------------------------------------------------------------ */

function TracePanel({ audit, callId }: { audit: Audit; callId: string }) {
  const { data } = useQuery(() => api.rawAi(callId), [callId]);

  return (
    <div className="stack">
      <Card title="How this audit was produced">
        <div className="grid grid-stats">
          <div>
            <div className="meta-label">Engine version</div>
            <div className="meta-value mono">{audit.engineVersion}</div>
          </div>
          <div>
            <div className="meta-label">Analysis provider</div>
            <div className="meta-value">
              {audit.aiProvider}
              {audit.aiProvider === 'heuristic' && <span className="pill pill-sm pill-warn" style={{ marginLeft: 6 }}>Local rules, not an LLM</span>}
            </div>
          </div>
          <div>
            <div className="meta-label">Model</div>
            <div className="meta-value mono">{audit.aiModel ?? '—'}</div>
          </div>
          <div>
            <div className="meta-label">Produced</div>
            <div className="meta-value">{formatDateTime(audit.generatedAt)}</div>
          </div>
        </div>

        <p className="muted mt-3">
          The analysis layer produced per-category assessments, extracted evidence and integrity findings. The overall score, the
          band, the critical-category cap, the BANT verdicts, the status mapping and the auto-fail decision were all calculated by
          the application from those inputs — no model was asked for them.
        </p>
      </Card>

      <Card title="Decision trace">
        <div className="stack" style={{ gap: 12 }}>
          <TraceRow
            decision={`Overall score: ${audit.overallScore ?? '—'}`}
            rule="Simple average of all scored (non-N/A) categories, rounded to 1 decimal."
            detail={`${audit.scoring.scoredCount} scored, ${audit.scoring.naCount} N/A.`}
          />
          <TraceRow decision={`Band: ${audit.scoring.rawBand}`} rule="4.5-5.0 Excellent · 3.5-4.4 Good · 2.5-3.4 Needs Work · 1.0-2.4 Poor." detail="" />
          {audit.scoring.capReason && <TraceRow decision="Critical-category cap" rule="Any critical category scored 1 caps the band at Needs Work." detail={audit.scoring.capReason} />}
          {audit.autoFail.triggered && <TraceRow decision="AUTO-FAIL" rule="An auto-fail trigger overrides the band entirely." detail={audit.autoFail.reason} />}
          <TraceRow decision={`Qualified booking: ${audit.bant.qualifiedBooking ? 'yes' : 'no'}`} rule={audit.bant.qualifiedBookingRule} detail={`B ${audit.bant.budget.verdict} · A ${audit.bant.authority.verdict} · N ${audit.bant.need.verdict} · T ${audit.bant.timeframe.verdict}`} />
          <TraceRow decision={`Auditor verdict: ${audit.status.agentVerdictStatus}`} rule={audit.status.verdictRule} detail={audit.status.verdictReason} />
        </div>
      </Card>

      <Card title="Raw analysis output" note="Retained for debugging and dispute">
        {!data?.outputs.length ? (
          <p className="muted">No raw output is on file for this audit.</p>
        ) : (
          <div className="stack" style={{ gap: 5 }}>
            {data.outputs.map((o, i) => (
              <details className="raw" key={i}>
                <summary>
                  {titleCase(o.stage)} · {o.provider}/{o.model} · {o.latency_ms}ms {o.error ? '· ERROR' : ''}
                </summary>
                <pre>{o.error ?? o.response_text}</pre>
              </details>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function TraceRow({ decision, rule, detail }: { decision: string; rule: string; detail: string }) {
  return (
    <div className="limitation" style={{ borderLeftColor: 'var(--c-accent)' }}>
      <div className="limitation-area">{decision}</div>
      <div className="muted small">
        <strong>Rule:</strong> {rule}
      </div>
      {detail && <div className="limitation-impact">{detail}</div>}
    </div>
  );
}

export type { CategoryAssessment };
